import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from 'styles/theme';

const NumberInput = styled.input`
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 2px 4px;
  box-sizing: border-box;
  font-family: var(--erp-grid-font-family, ${theme.typography.gridFontFamily});
  font-weight: 400;
  text-align: right;
  /* 스피너(상·하 화살표) 숨김 */
  appearance: textfield;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

/**
 * TanStack 그리드 셀 - 숫자 인라인 에디터
 * - 스피너 비표시 · 휠/↑↓ 키로 값 변경 방지
 */
const NumberCellEditor = ({
  value,
  initialKey,
  onSave,
  onCancel,
  onBoundaryArrow,
  onEnterMoveDown,
  onEnterMoveUp,
  onTabMove,
  onFillSelection,
}) => {
  const inputRef = useRef(null);
  const aliveRef = useRef(true);
  const graceUntilRef = useRef(0);
  const parseValue = (raw) => (raw === '' ? null : Number(raw));

  useEffect(() => {
    aliveRef.current = true;
    graceUntilRef.current = Date.now() + 250;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const handleKeyDown = (e) => {
    // 화살표 상·하로 숫자 step 변경 방지
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const parsed = parseValue(e.target.value);
      if (e.ctrlKey || e.metaKey) {
        onSave?.(parsed);
        onFillSelection?.(parsed);
        return;
      }
      onSave?.(parsed, { focusGrid: false });
      if (e.shiftKey) onEnterMoveUp?.();
      else onEnterMoveDown?.();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      onSave?.(parseValue(e.target.value), { focusGrid: false });
      onTabMove?.(e.shiftKey ? 'left' : 'right');
      return;
    }
    if (e.key === 'Escape') {
      onCancel?.();
      return;
    }
    const input = e.target;
    const v = input.value ?? '';
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    if (e.key === 'ArrowLeft' && start === 0 && end === 0) {
      e.preventDefault();
      onSave?.(parseValue(v), { focusGrid: false });
      onBoundaryArrow?.('left');
      return;
    }
    if (e.key === 'ArrowRight' && start === v.length && end === v.length) {
      e.preventDefault();
      onSave?.(parseValue(v), { focusGrid: false });
      onBoundaryArrow?.('right');
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  const handleBlur = (e) => {
    const raw = e.target.value;
    window.setTimeout(() => {
      if (!aliveRef.current) return;
      if (document.activeElement === inputRef.current) return;
      if (Date.now() < graceUntilRef.current) {
        inputRef.current?.focus();
        return;
      }
      onSave?.(parseValue(raw), { focusGrid: false });
    }, 0);
  };

  const handleWheel = (e) => {
    // 포커스 중 휠로 금액 변동 방지
    e.preventDefault();
  };

  return (
    <NumberInput
      ref={inputRef}
      type="number"
      defaultValue={initialKey != null ? initialKey : (value ?? '')}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      autoFocus
    />
  );
};

export default NumberCellEditor;
